/**
 * Pattern Kit Selector
 * Streamlined yarn selection for pattern products
 */

class PatternKitSelector {
  constructor() {
    this.container = document.getElementById('pattern-kit-selector');
    if (!this.container) return;

    this.yarnRequirements = JSON.parse(this.container.dataset.yarnRequirements);
    this.patternName = this.container.dataset.patternName || '';
    this.sizeSelect = document.getElementById('kit-size-select');
    this.yarnCards = document.querySelectorAll('.kit-yarn-card');
    this.addToCartBtn = document.getElementById('kit-add-to-cart');
    this.modal = document.getElementById('kit-color-modal');

    this.selections = {}; // Store selected variants per yarn index
    this.activeCardIndex = null;

    // Load translations
    this.translations = this.loadTranslations();

    this.init();
  }

  /**
   * Load translations from data attribute
   */
  loadTranslations() {
    const defaults = {
      sizeLabel: 'FÜR WELCHE GRÖSSE BENÖTIGEN SIE DAS GARN?',
      sizeSelect: 'Größe wählen',
      selectColor: 'Farbe wählen',
      skeins: 'Knäuel',
      currency: 'EUR',
      optionalAccessories: 'Optionales Zubehör',
      circularNeedles: 'Rundstricknadeln',
      selectNeedle: 'Nadel auswählen...',
      addToCart: 'IN DEN WARENKORB LEGEN',
      close: 'Schließen',
      searchColor: 'Farbe suchen...',
      available: 'verfügbar',
      selectSizeFirst: 'Bitte wählen Sie zuerst eine Größe aus.',
      colorsHidden: 'Farbe(n) ausgeblendet (nicht genügend Lagerbestand für',
      adding: 'WIRD HINZUGEFÜGT...',
      partiallyAdded: '⚠ TEILWEISE HINZUGEFÜGT',
      added: '✓ HINZUGEFÜGT!',
      error: 'FEHLER - ERNEUT VERSUCHEN',
      patternLabel: 'Anleitung',
      usageLabel: 'Verwendung',
      sizeLabel2: 'Größe',
      variantLabel: 'Variante'
    };

    try {
      const translationsData = this.container.dataset.translations;
      if (translationsData) {
        return { ...defaults, ...JSON.parse(translationsData) };
      }
    } catch (e) {
      console.warn('Pattern Kit: Failed to parse translations');
    }

    return defaults;
  }

  init() {
    // Move modal to body to prevent positioning issues
    if (this.modal && this.modal.parentElement !== document.body) {
      document.body.appendChild(this.modal);
    }

    this.bindEvents();
    this.bindOptionalAccessories();
    this.updateTotalPrice();
  }

  bindEvents() {
    // Size selector
    if (this.sizeSelect) {
      this.sizeSelect.addEventListener('change', () => this.handleSizeChange());
    }

    // Yarn cards
    this.yarnCards.forEach((card, index) => {
      card.addEventListener('click', () => {
        // Check if size is selected first (if size-based)
        if (this.yarnRequirements.size_based && this.sizeSelect && !this.sizeSelect.value) {
          alert(this.translations.selectSizeFirst);
          this.sizeSelect.focus();
          return;
        }
        this.openColorModal(index);
      });
    });

    // Modal controls
    if (this.modal) {
      const backdrop = this.modal.querySelector('.modal-backdrop');
      const closeBtn = this.modal.querySelector('.modal-close');

      backdrop.addEventListener('click', () => this.closeModal());
      closeBtn.addEventListener('click', () => this.closeModal());

      // Color options
      const colorOptions = this.modal.querySelectorAll('.color-option');
      colorOptions.forEach(option => {
        option.addEventListener('click', () => this.selectColor(option));
      });

      // Search
      const searchInput = this.modal.querySelector('.color-search-input');
      searchInput.addEventListener('input', (e) => this.filterColors(e.target.value));
    }

    // Add to cart
    this.addToCartBtn.addEventListener('click', () => this.addToCart());
  }

  bindOptionalAccessories() {
    // Handle optional item checkboxes
    const optionalCheckboxes = document.querySelectorAll('.optional-checkbox');
    optionalCheckboxes.forEach(checkbox => {
      checkbox.addEventListener('change', () => this.updateTotalPrice());
    });

    // Handle needle selection
    const needleCheckbox = document.querySelector('.optional-needles-checkbox');
    const needleSelector = document.querySelector('.optional-needles-selector');
    const needleSelect = document.querySelector('.needles-select');

    if (needleCheckbox && needleSelector && needleSelect) {
      needleCheckbox.addEventListener('change', (e) => {
        if (e.target.checked) {
          needleSelector.style.display = 'block';
        } else {
          needleSelector.style.display = 'none';
          needleSelect.value = '';
          this.updateNeedlePrice();
        }
        this.updateTotalPrice();
      });

      needleSelect.addEventListener('change', () => {
        this.updateNeedlePrice();
        this.updateTotalPrice();
      });
    }
  }

  updateNeedlePrice() {
    const needleSelect = document.querySelector('.needles-select');
    const needlePriceSpan = document.querySelector('.optional-needles-price');
    const needleVariantInput = document.querySelector('.kit-needles-item .optional-variant-id');

    if (!needleSelect || !needlePriceSpan || !needleVariantInput) return;

    const selectedOption = needleSelect.options[needleSelect.selectedIndex];

    if (selectedOption && selectedOption.value) {
      const price = parseInt(selectedOption.dataset.price) || 0;
      needlePriceSpan.textContent = this.formatMoney(price);
      needleVariantInput.value = selectedOption.value;
    } else {
      needlePriceSpan.textContent = '-';
      needleVariantInput.value = '';
    }
  }

  handleSizeChange() {
    const selectedSize = this.sizeSelect.value;
    if (!selectedSize) return;

    // Find size data
    const sizeData = this.yarnRequirements.sizes.find(s => s.name === selectedSize);
    if (!sizeData) return;

    // Update quantities for each yarn card
    this.yarnCards.forEach((card, index) => {
      const yarnLabel = card.dataset.yarnLabel;
      const quantity = sizeData.quantities[yarnLabel] || 0;

      // Update hidden input
      card.querySelector('.selected-quantity').value = quantity;

      // Update display
      const quantitySpan = card.querySelector('.yarn-quantity');
      quantitySpan.textContent = `${quantity} ${this.translations.skeins}`;
    });

    // Reset color selections when size changes (but keep quantities)
    this.selections = {};
    this.yarnCards.forEach(card => {
      this.resetCardColor(card);
    });

    this.updateTotalPrice();
  }

  openColorModal(cardIndex) {
    this.activeCardIndex = cardIndex;
    const card = this.yarnCards[cardIndex];
    const yarnType = card.dataset.yarnType;
    const yarnLabel = card.dataset.yarnLabel;

    // Get required quantity for this yarn card
    const requiredQuantity = parseInt(card.querySelector('.selected-quantity').value) || 0;

    // Update modal title
    const modalTitle = this.modal.querySelector('.modal-title');
    modalTitle.textContent = `${yarnLabel} - ${this.translations.selectColor}`;

    // Filter colors by yarn type AND inventory
    const colorOptions = this.modal.querySelectorAll('.color-option');
    let availableCount = 0;
    let unavailableCount = 0;

    colorOptions.forEach(option => {
      const optionYarnType = option.dataset.yarnType;
      const optionInventory = parseInt(option.dataset.inventory) || 999;

      if (optionYarnType === yarnType) {
        // Check if there's enough inventory
        const hasEnoughStock = optionInventory >= requiredQuantity;

        if (hasEnoughStock) {
          option.style.display = '';
          option.classList.remove('out-of-stock');
          availableCount++;

          // Highlight if currently selected
          if (this.selections[cardIndex] &&
              this.selections[cardIndex].variantId === option.dataset.variantId) {
            option.classList.add('selected');
          } else {
            option.classList.remove('selected');
          }
        } else {
          // Hide out of stock options
          option.style.display = 'none';
          option.classList.add('out-of-stock');
          unavailableCount++;
        }
      } else {
        option.style.display = 'none';
      }
    });

    // Show stock notice if some colors are unavailable
    if (unavailableCount > 0) {
      const existingNotice = this.modal.querySelector('.stock-availability-notice');
      if (existingNotice) existingNotice.remove();

      const notice = document.createElement('div');
      notice.className = 'stock-availability-notice';
      notice.textContent = `${unavailableCount} ${this.translations.colorsHidden} ${requiredQuantity} ${this.translations.skeins})`;

      const modalBody = this.modal.querySelector('.modal-body');
      modalBody.insertBefore(notice, modalBody.querySelector('.color-grid'));
    }

    // Show modal
    this.modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';

    // Reset search
    this.modal.querySelector('.color-search-input').value = '';
  }

  closeModal() {
    this.modal.style.display = 'none';
    document.body.style.overflow = '';
    this.activeCardIndex = null;

    // Clear search
    const searchInput = this.modal.querySelector('.color-search-input');
    searchInput.value = '';
    this.filterColors('');

    // Clear stock notice
    const existingNotice = this.modal.querySelector('.stock-availability-notice');
    if (existingNotice) existingNotice.remove();

    // Clear selections
    this.modal.querySelectorAll('.color-option').forEach(opt => {
      opt.classList.remove('selected');
    });
  }

  selectColor(option) {
    if (this.activeCardIndex === null) return;

    const card = this.yarnCards[this.activeCardIndex];
    const variantId = option.dataset.variantId;
    const variantTitle = option.dataset.variantTitle;
    const variantPrice = parseInt(option.dataset.variantPrice);
    const quantity = parseInt(card.querySelector('.selected-quantity').value) || 0;

    // Store selection
    this.selections[this.activeCardIndex] = {
      variantId,
      variantTitle,
      variantPrice,
      quantity
    };

    // Update card display
    this.updateCardDisplay(card, option, variantId, variantTitle, variantPrice, quantity);

    // Update total
    this.updateTotalPrice();

    // Close modal
    this.closeModal();
  }

  updateCardDisplay(card, option, variantId, title, price, quantity) {
    // Update image
    const colorPreview = card.querySelector('.yarn-color-preview');
    const img = option.querySelector('img');
    if (img) {
      colorPreview.innerHTML = `<img src="${img.src}" alt="${title}" style="width: 100%; height: 100%; object-fit: cover; border-radius: inherit;">`;
    }

    // Update text
    card.querySelector('.yarn-color-name').textContent = title;
    card.querySelector('.yarn-quantity').textContent = `${quantity} ${this.translations.skeins}`;
    card.querySelector('.yarn-price').textContent = this.formatMoney(price);

    // Update hidden input
    card.querySelector('.selected-variant-id').value = variantId;

    // Add selected class
    card.classList.add('selected');
  }

  resetCardColor(card) {
    // Reset only the color selection, keeping quantity
    const colorPreview = card.querySelector('.yarn-color-preview');
    colorPreview.innerHTML = `
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <circle cx="12" cy="12" r="10" stroke-width="2" stroke-dasharray="2 2"/>
      </svg>
    `;

    // Reset color name and price
    card.querySelector('.yarn-color-name').textContent = this.translations.selectColor;
    card.querySelector('.yarn-price').textContent = `- ${this.translations.currency}`;

    // Reset variant ID only (keep quantity)
    card.querySelector('.selected-variant-id').value = '';

    // Remove selected class
    card.classList.remove('selected');
  }

  resetCard(card) {
    // Full reset including quantity
    this.resetCardColor(card);

    // Reset quantity
    card.querySelector('.yarn-quantity').textContent = `- ${this.translations.skeins}`;
    card.querySelector('.selected-quantity').value = '';
  }

  filterColors(searchTerm) {
    const term = searchTerm.toLowerCase().trim();
    const colorOptions = this.modal.querySelectorAll('.color-option');

    colorOptions.forEach(option => {
      if (option.style.display === 'none') return; // Already filtered by yarn type

      const searchText = option.dataset.search;
      const matches = !term || searchText.includes(term);

      if (matches) {
        option.classList.remove('hidden');
      } else {
        option.classList.add('hidden');
      }
    });
  }

  updateTotalPrice() {
    let total = 0;
    let allSelected = true;

    // Calculate yarn total
    this.yarnCards.forEach((card, index) => {
      const selection = this.selections[index];
      if (selection) {
        total += selection.variantPrice * selection.quantity;
      } else {
        allSelected = false;
      }
    });

    // Add optional accessories to total
    const optionalItems = document.querySelectorAll('.kit-optional-item');
    optionalItems.forEach(item => {
      const checkbox = item.querySelector('.optional-checkbox');
      if (checkbox && checkbox.checked) {
        const variantId = item.querySelector('.optional-variant-id').value;
        const priceText = item.querySelector('.optional-item-price').textContent;

        // Extract price from formatted string (e.g., "€9,50" -> 950)
        if (variantId) {
          // For needles with variants, get price from select option
          if (item.classList.contains('kit-needles-item')) {
            const select = item.querySelector('.needles-select');
            if (select && select.value) {
              const selectedOption = select.options[select.selectedIndex];
              const price = parseInt(selectedOption.dataset.price) || 0;
              total += price;
            }
          } else {
            // For simple items like stitch markers
            const priceMatch = priceText.match(/[\d,]+/);
            if (priceMatch) {
              const priceValue = parseFloat(priceMatch[0].replace(',', '.'));
              total += Math.round(priceValue * 100);
            }
          }
        }
      }
    });

    // Check if size is selected (if size-based)
    if (this.yarnRequirements.size_based && this.sizeSelect && !this.sizeSelect.value) {
      allSelected = false;
    }

    // Update button
    const btnTotal = this.addToCartBtn.querySelector('.btn-total');
    btnTotal.textContent = this.formatMoney(total);

    // Enable/disable button
    this.addToCartBtn.disabled = !allSelected;
  }

  async addToCart() {
    if (this.addToCartBtn.disabled) return;

    const btnText = this.addToCartBtn.querySelector('.btn-text');
    const originalText = btnText.textContent;

    this.addToCartBtn.disabled = true;
    btnText.textContent = this.translations.adding;

    try {
      // Build items array
      const items = [];
      const selectedSize = this.sizeSelect ? this.sizeSelect.value : null;

      // Add main yarn items
      this.yarnCards.forEach((card, index) => {
        const selection = this.selections[index];
        if (selection) {
          const properties = {};
          properties[this.translations.patternLabel] = this.patternName;
          properties[this.translations.usageLabel] = card.dataset.yarnLabel;

          // Add size if selected
          if (selectedSize) {
            properties[this.translations.sizeLabel2] = selectedSize;
          }

          const item = {
            id: selection.variantId,
            quantity: selection.quantity,
            properties: properties
          };
          items.push(item);
        }
      });

      // Add optional accessories
      const optionalItems = document.querySelectorAll('.kit-optional-item');
      optionalItems.forEach(optItem => {
        const checkbox = optItem.querySelector('.optional-checkbox');
        if (checkbox && checkbox.checked) {
          const variantId = optItem.querySelector('.optional-variant-id').value;
          const quantity = parseInt(optItem.querySelector('.optional-quantity').value) || 1;

          if (variantId) {
            const properties = {};
            properties[this.translations.patternLabel] = this.patternName;

            if (selectedSize) {
              properties[this.translations.sizeLabel2] = selectedSize;
            }

            // Add title for needles
            if (optItem.classList.contains('kit-needles-item')) {
              const select = optItem.querySelector('.needles-select');
              if (select && select.value) {
                const selectedOption = select.options[select.selectedIndex];
                properties[this.translations.variantLabel] = selectedOption.dataset.title;
              }
            }

            items.push({
              id: variantId,
              quantity: quantity,
              properties: properties
            });
          }
        }
      });

      // Add to cart via Shopify API
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ items })
      });

      const responseData = await response.json();

      if (!response.ok) {
        // Handle specific Shopify errors
        if (response.status === 422 && responseData.description) {
          // Partial success or inventory limitation
          btnText.textContent = this.translations.partiallyAdded;
          this.addToCartBtn.style.background = '#ff9800';

          // Show Shopify's message to user
          alert(responseData.description);

          // Trigger cart update (items may have been partially added)
          document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
            bubbles: true
          }));

          // Reset form after partial add to prevent re-adding
          setTimeout(() => {
            this.resetForm();
            btnText.textContent = originalText;
            this.addToCartBtn.style.background = '';
          }, 2000);

          return; // Don't throw error
        } else {
          throw new Error(`Failed to add to cart: ${response.status} - ${JSON.stringify(responseData)}`);
        }
      }

      // Show success
      btnText.textContent = this.translations.added;
      this.addToCartBtn.style.background = '#4caf50';

      // Trigger cart update event
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
        bubbles: true
      }));

      // Reset form after successful add to prevent duplicate additions
      setTimeout(() => {
        this.resetForm();
        btnText.textContent = originalText;
        this.addToCartBtn.style.background = '';
      }, 1500);

    } catch (error) {
      console.error('Error adding to cart:', error);

      btnText.textContent = this.translations.error;
      this.addToCartBtn.style.background = '#f44336';

      setTimeout(() => {
        btnText.textContent = originalText;
        this.addToCartBtn.style.background = '';
        this.addToCartBtn.disabled = false;
      }, 3000);
    }
  }

  resetForm() {
    // Reset size
    if (this.sizeSelect) {
      this.sizeSelect.value = '';
    }

    // Reset selections
    this.selections = {};

    // Reset all cards
    this.yarnCards.forEach(card => {
      this.resetCard(card);
    });

    // Reset optional accessories
    const optionalCheckboxes = document.querySelectorAll('.optional-checkbox');
    optionalCheckboxes.forEach(checkbox => {
      checkbox.checked = false;
    });

    const needleSelector = document.querySelector('.optional-needles-selector');
    const needleSelect = document.querySelector('.needles-select');
    if (needleSelector) {
      needleSelector.style.display = 'none';
    }
    if (needleSelect) {
      needleSelect.value = '';
    }
    this.updateNeedlePrice();

    // Update total
    this.updateTotalPrice();
  }

  formatMoney(cents) {
    const euros = (cents / 100).toFixed(2);
    return `${euros.replace('.', ',')} ${this.translations.currency}`;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new PatternKitSelector();
  });
} else {
  new PatternKitSelector();
}
